import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, MenuItem, Skeleton, Snackbar, Stack, TextField,
  Typography,
} from '@mui/material';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import GedChatButton from '../GedChat/GedChatButton';

const STATUTS = ['Préparation', 'Contradictoire', 'Clôture', 'Annuler'];

const VIDE = { name: '', reference: '', statut: '', codeName: '' };

/** La fiche d'identité du dossier. */
function EditExpertise({ onDossierModifie }) {
  const { expertiseId } = useParams();

  const [dossier, setDossier] = useState(VIDE);
  const [initial, setInitial] = useState(VIDE);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [enregistre, setEnregistre] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get(`/expertise/${expertiseId}/`);
      const lu = {
        name: data.name || '', reference: data.reference || '',
        statut: data.statut || '', codeName: data.codeName || '',
      };
      setDossier(lu);
      setInitial(lu);
      setErreur('');
    } catch {
      setErreur("Le dossier n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  const modifier = (champ) => (e) => {
    setDossier((etat) => ({ ...etat, [champ]: e.target.value }));
    if (erreur) setErreur('');
  };

  const modifie = JSON.stringify(dossier) !== JSON.stringify(initial);

  const soumettre = async (e) => {
    e.preventDefault();
    setOccupe(true);
    setErreur('');
    try {
      // PATCH et non PUT : on n'envoie que les quatre champs de cette fiche,
      // et un PUT prétendrait remplacer le dossier entier.
      const { data } = await axiosInstance.patch(`/expertise/${expertiseId}/`, {
        name: dossier.name.trim(),
        reference: dossier.reference,
        statut: dossier.statut,
      });
      const lu = { ...dossier, codeName: data.codeName || dossier.codeName };
      setDossier(lu);
      setInitial(lu);
      setEnregistre(true);
      // L'en-tête de l'écran porte le nom du dossier : il doit suivre.
      if (onDossierModifie) onDossierModifie();
    } catch (error) {
      setErreur(error.response?.data?.name?.[0]
        || "Les modifications n'ont pas pu être enregistrées.");
    } finally {
      setOccupe(false);
    }
  };

  if (chargement) {
    return <Stack spacing={2}>
      <Skeleton height={56} /><Skeleton height={56} /><Skeleton height={120} />
    </Stack>;
  }

  return (
    <Stack spacing={3}>
      <Fiche titre="Identité du dossier"
             explication="Ce que vous voyez en tête de chaque écran et dans la liste.">
        <Box component="form" onSubmit={soumettre} noValidate>
          {erreur && <Alert severity="error" sx={{ mb: 2.5 }}>{erreur}</Alert>}

          <Stack spacing={2.5}>
            <TextField
              label="Nom du dossier"
              value={dossier.name}
              onChange={modifier('name')}
              required
              disabled={occupe}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Votre référence"
                value={dossier.reference}
                onChange={modifier('reference')}
                disabled={occupe}
                helperText="Numéro de sinistre ou code interne."
              />

              <TextField
                select
                label="Statut"
                value={dossier.statut}
                onChange={modifier('statut')}
                required
                disabled={occupe}
                helperText={dossier.statut === 'Annuler'
                  ? "Le dossier sortira de la liste, sans être effacé."
                  : ' '}
              >
                <MenuItem value=""><em>Aucun</em></MenuItem>
                {STATUTS.map((statut) => (
                  <MenuItem key={statut} value={statut}>{statut}</MenuItem>
                ))}
              </TextField>
            </Stack>

            {dossier.codeName && (
              <Typography variant="caption" color="text.secondary">
                Code du dossier : {dossier.codeName} — attribué à la création,
                il ne change pas.
              </Typography>
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={() => setDossier(initial)}
                      disabled={occupe || !modifie}>
                Annuler les modifications
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !modifie || !dossier.name.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Fiche>

      <Fiche titre="Assistant du dossier"
             explication="Il répond à partir des pièces déposées ici, et cite ce sur quoi il s'appuie.">
        <GedChatButton expertiseId={expertiseId}
                       label="Ouvrir l'assistant" />
      </Fiche>

      <Snackbar
        open={enregistre}
        autoHideDuration={3000}
        onClose={() => setEnregistre(false)}
        message="Modifications enregistrées"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}

export default EditExpertise;
